import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, LinearProgress, Skeleton, Tooltip, Typography } from "@mui/material";
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PostAddIcon from '@mui/icons-material/PostAdd';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import PageTitle from "../PageTitle";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import { apiConstants, patchGeneric, postGeneric } from "../../common/constants";
import { parseDateAndTime } from "../../helpers/UtilityHelper";
import { useSnackbar } from "notistack";
import { LoadingButton } from "@mui/lab";
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CreateEscalationTicket from "./CreateEscalationTicket";
import BulkCreateModal from "./BulkCreateModal";
import BulkUpdateModal from "./BulkUpdateModal";
import ExportTicketsModal from "./ExportTicketsModal";
import EscalationV2Nav from "./EscalationV2Nav";
import EscalationFilterBar from "./EscalationFilterBar";
import useEscalationFilterBar from "./useEscalationFilterBar";
import { DRILLDOWN_LABELS, UNASSIGNED_OPTION, searchParamsToFilters } from "./escalationFilterUtils";
import { gradientBtnSx } from "./SectionCard";
import EmsPageHeader from "./EmsPageHeader";
import { ACCENTS, CARD_SX, COLORS, rise } from "./emsTheme";
import TicketsSummaryStrip, { STRIP_FLAGS } from "./TicketsSummaryStrip";
import { PLATFORMS, PLATFORM_META, PLATFORM_TABS, platformOf } from "./platform";
import {
    DIALOG_PAPER_SX, SEVERITY_TONE, SeverityPill, StatusPill, TagPill, TatBadge, TicketsDialogTitle, pillBtnSx, softIconBtnSx, tatState,
} from "./TicketsUi";

const ROW_SX = {
    // Header text: 12px minimum (the shared grid uses 11px).
    '& .MuiDataGrid-columnHeaders': { bgcolor: '#FBFAFD', borderBottom: `1px solid ${COLORS.grid}` },
    '& .MuiDataGrid-columnHeaderTitle': { fontSize: 12, fontWeight: 700, color: COLORS.ink3, letterSpacing: '.04em' },
    '& .MuiDataGrid-row': { borderBottom: `1px solid #EFEDF3` },
    '& .MuiDataGrid-row:hover, & .prozo-stripe:hover': { backgroundColor: '#F7F3FB !important' },
    '& .MuiDataGrid-row.Mui-selected, & .prozo-stripe.Mui-selected': { backgroundColor: `${COLORS.purpleSoft} !important` },
    '& .MuiDataGrid-row.Mui-selected:hover': { backgroundColor: '#E9E0F3 !important' },
    '& .prozo-stripe': { backgroundColor: '#FCFBFD' },
    // An open ticket past its TAT gets a thin red edge, so it stands out while scanning.
    '& .ems-row-past': { boxShadow: `inset 3px 0 0 ${ACCENTS.red.solid}` },
    '& .MuiCheckbox-root.Mui-checked, & .MuiCheckbox-root.MuiCheckbox-indeterminate': { color: COLORS.purple },
    '& .MuiDataGrid-cell': { py: 1 },
    '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: 13 },
};

/** Includes the page's default size (30), so the grid keeps its rows-per-page selector. */
const ROWS_PER_PAGE_OPTIONS = [10, 20, 30, 50, 100];

/** The flag the Open/Closed tab adds to the list's filters (and to an export of it). */
const tabFilterFor = (tab) => (tab === 'OPEN' ? { unresolvedOnly: true } : { closedOnly: true });
/** The flag the Proship / WMS / CT tab adds to the list's filters (and to its counts and export); All adds none. */
const platformFilterFor = (platform) => (platform === 'ALL' ? {} : { platforms: [platform] });

const viewPath = (row) => `/tools/escalation-v2/view?ticketId=${row.id}`;
const dash = <Typography component="span" sx={{ fontSize: 13, color: COLORS.ink3 }}>-</Typography>;

/** Grid with more rows than this scrolls inside a bounded box, so its header stays in view. */
const SCROLL_AFTER_ROWS = 10;

const SkeletonRows = ({ rows = 6 }) => (
    <Box aria-busy="true" aria-label="Loading tickets" sx={{ px: 2, py: 1 }}>
        <Box sx={{ display: 'flex', gap: 2, py: 1.25, borderBottom: `1px solid ${COLORS.grid}` }}>
            {[80, 130, 60, 120, 110, 140].map((w, i) => <Skeleton key={i} width={w} height={18} />)}
        </Box>
        {Array.from({ length: rows }).map((_, r) => (
            <Box key={r} sx={{ display: 'flex', gap: 2, alignItems: 'center', py: 1.5, borderBottom: `1px solid #F1EFF5`, opacity: 1 - r * 0.12 }}>
                <Skeleton variant="rounded" width={18} height={18} />
                <Skeleton width={70} height={18} />
                <Skeleton width={120} height={18} />
                <Skeleton variant="rounded" width={34} height={22} sx={{ borderRadius: '999px' }} />
                <Skeleton width={110} height={18} sx={{ display: { xs: 'none', sm: 'block' } }} />
                <Skeleton variant="rounded" width={120} height={22} sx={{ borderRadius: '999px', display: { xs: 'none', md: 'block' } }} />
                <Skeleton width={130} height={18} sx={{ display: { xs: 'none', md: 'block' } }} />
            </Box>
        ))}
    </Box>
);

const EmptyState = ({ tab, drilled, onClear }) => (
    <Box sx={{ py: { xs: 5, md: 7 }, px: 2, display: 'grid', justifyItems: 'center', textAlign: 'center', gap: 1, ...rise(0) }}>
        <Box aria-hidden sx={{ width: 56, height: 56, borderRadius: '16px', display: 'grid', placeItems: 'center', mb: 0.5,
            bgcolor: tab === 'OPEN' && !drilled ? ACCENTS.green.soft : COLORS.purpleSoft }}>
            {tab === 'OPEN' && !drilled
                ? <TaskAltIcon sx={{ fontSize: 30, color: ACCENTS.green.solid }} />
                : <InboxOutlinedIcon sx={{ fontSize: 30, color: COLORS.purple }} />}
        </Box>
        <Typography sx={{ fontSize: 16, fontWeight: 800, color: COLORS.ink }}>
            {tab === 'OPEN' ? (drilled ? 'No open tickets match this view' : 'No open tickets for these filters') : 'No closed tickets for these filters'}
        </Typography>
        <Typography sx={{ fontSize: 13, color: COLORS.ink3, maxWidth: 420 }}>
            {drilled ? 'Clear the highlighted summary filter above, or widen the date range.' : 'Try a wider date range or fewer filters above.'}
        </Typography>
        {drilled && <Button size="small" variant="outlined" onClick={onClear} sx={{ ...pillBtnSx, mt: 0.5 }}>Clear summary filter</Button>}
    </Box>
);

/** Thin progress bar over the rows while a page, tab or filter change loads. */
const LoadingOverlay = () => (
    <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,.55)', zIndex: 2 }}>
        <LinearProgress sx={{ height: 3, bgcolor: COLORS.purpleSoft, '& .MuiLinearProgress-bar': { background: ACCENTS.purple.grad } }} />
    </Box>
);

/**
 * Escalation Management V2 — Tickets page. Reads from the new in-house /api/escalation/*
 * backend (see proship_mongo's escalation module) instead of the Freshdesk proxy the V1 page
 * ("Escalation Management") calls. Both pages run side by side during the rollout. Clicking a
 * ticket # goes to ViewTicket.js (remarks, status change, status history). The Analytics tab
 * (AnalyticsPage.js) is a separate page under the same EscalationV2Nav — see its docstring for
 * why, and escalationFilterUtils for how a drill-down there hands filters off to this page.
 */
const EscalationManagementV2 = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(30);
    const [resData, setResData] = useState({ results: [], total: 0 });
    const [isLoading, setIsLoading] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [reloadTick, setReloadTick] = useState(0);
    const [rowSelectionModel, setRowSelectionModel] = useState([]);
    const [bulkConfirm, setBulkConfirm] = useState(null); // 'CLOSED' | 'OPEN' | null
    const [quickConfirm, setQuickConfirm] = useState(null); // { row, targetCategory } | null
    const [quickProcessing, setQuickProcessing] = useState(false);
    const [bulkProcessing, setBulkProcessing] = useState(false);
    const [bulkCreateOpen, setBulkCreateOpen] = useState(false);
    const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
    const [exportOpen, setExportOpen] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    // A drill-down handed off from the Analytics page arrives as query params (they're separate
    // routes, so this is the only way to carry filters across the navigation) — read once, on
    // mount, to seed the filter bar instead of its usual "today" default. Later filter-bar
    // interactions (Filter/Reset) manage their own state independent of the URL from here on.
    // unresolvedOnly/closedOnly specifically aren't kept in the filter bar's own state at all —
    // they're what the Open/Closed tab bar below represents (see activeTicketTab), so they're
    // stripped here and translated into which tab starts selected instead.
    const { initialFilters, initialTab, tabFromCounts, initialPlatform } = useMemo(() => {
        const params = new URLSearchParams(location.search);
        if (!params.toString()) return { initialFilters: undefined, initialTab: 'OPEN', tabFromCounts: false, initialPlatform: 'ALL' };
        const parsed = searchParamsToFilters(params);
        // A platform picked on Analytics opens that platform's tab; the tab carries it from here.
        const picked = (parsed.platforms || []).filter((p) => PLATFORMS.includes(p));
        delete parsed.platforms;
        const closedHint = parsed.closedOnly || parsed.statusCategory === 'RESOLVED' || parsed.statusCategory === 'CLOSED';
        const openHint = parsed.unresolvedOnly || parsed.statusCategory === 'OPEN' || parsed.statusCategory === 'PENDING';
        const tab = closedHint ? 'CLOSED' : 'OPEN';
        delete parsed.unresolvedOnly;
        delete parsed.closedOnly;
        // Drill-downs that can land on either tab (e.g. Reopened, a breakdown row): let the
        // first badge counts decide, so the page doesn't open on an empty tab.
        return { initialFilters: parsed, initialTab: tab, tabFromCounts: !closedHint && !openHint, initialPlatform: picked.length === 1 ? picked[0] : 'ALL' };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const bar = useEscalationFilterBar(initialFilters);
    const { appliedFilters, setAppliedFilters, statusList } = bar;
    // Mirrors V1's EscalationManagement Open/Closed tabs. Deliberately NOT stored inside
    // appliedFilters — that way clicking Filter/Reset (which replaces appliedFilters wholesale)
    // can never silently wipe out which tab is selected; the tab's flag is merged in only at
    // fetch time, below.
    const [activeTicketTab, setActiveTicketTab] = useState(initialTab);
    const [openCount, setOpenCount] = useState(null);
    const [closedCount, setClosedCount] = useState(null);
    const tabFromCountsPending = useRef(tabFromCounts);
    // All / Proship / WMS / CT: the system the tickets were raised from. Kept out of appliedFilters for
    // the same reason as the Open/Closed tab, and merged in at fetch time.
    const [activePlatform, setActivePlatform] = useState(initialPlatform);
    const [platformCounts, setPlatformCounts] = useState(null);
    const platformFilter = platformFilterFor(activePlatform);

    // One-time only: if a drill-down arrived with nothing open but something closed (e.g. a
    // ticket that was reopened and has since been closed again), show the Closed tab instead of
    // an empty Open tab. After that, the tab only changes when the user clicks it.
    useEffect(() => {
        if (!tabFromCountsPending.current || openCount == null || closedCount == null) return;
        tabFromCountsPending.current = false;
        if (openCount === 0 && closedCount > 0) setActiveTicketTab('CLOSED');
    }, [openCount, closedCount]);

    const onSubmit = (values) => {
        setPage(0);
        setAppliedFilters(bar.applyFilters(values));
    };
    const onReset = () => {
        setPage(0);
        bar.resetFilters();
    };
    // Switching tabs only swaps Open <-> Closed. Every other filter — the date range, the filter
    // bar, and any drill-down from Analytics — stays, so both tabs and both badges always
    // describe the same set of tickets (see EscalationService.getTabCounts).
    const selectTicketTab = (tab) => {
        setActiveTicketTab(tab);
        setPage(0);
        // The bulk buttons differ per tab, so a selection made on one tab never carries over.
        setRowSelectionModel([]);
    };
    const selectPlatform = (platform) => {
        setActivePlatform(platform);
        setPage(0);
        setRowSelectionModel([]);
    };

    // Summary strip drill-downs (Past TAT / Due today / Unassigned). Only one at a time; the rest
    // of the filters, date range included, carry over. They're all about open tickets, so they
    // land on the Open tab. Clicking the applied one again — or "Open" — clears it.
    const activeStripKey = STRIP_FLAGS.find((k) => appliedFilters?.[k] === true) || null;
    const selectStrip = useCallback((key, active) => {
        if (!key && !STRIP_FLAGS.some((k) => appliedFilters?.[k]) && activeTicketTab === 'OPEN') return; // already showing it
        const next = { ...appliedFilters };
        STRIP_FLAGS.forEach((k) => delete next[k]);
        if (key && !active) {
            next[key] = true;
            // Unassigned and a named assignee can never both match.
            if (key === 'unassignedOnly') { delete next.assignedToUserId; delete next.assignedToUserIds; delete next.assignedOnly; }
        }
        // The Assignee field shows unassignedOnly as "Unassigned"; keep it in step, so the next
        // Filter click doesn't undo this one.
        const assignees = bar.filterMethods.getValues('assignee') || [];
        if (next.unassignedOnly) bar.filterMethods.setValue('assignee', [UNASSIGNED_OPTION]);
        else if (assignees.includes(UNASSIGNED_OPTION)) bar.filterMethods.setValue('assignee', assignees.filter((a) => a !== UNASSIGNED_OPTION));
        setPage(0);
        setAppliedFilters(next);
        if (key || activeTicketTab !== 'OPEN') selectTicketTab('OPEN');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedFilters, activeTicketTab]);

    // Drill-down filters from an Analytics click that the filter bar doesn't show (the strip shows
    // its own three). Listed above the table so a narrowed list always says why; each can be removed.
    const drillChips = [
        ...Object.keys(DRILLDOWN_LABELS).filter((k) => appliedFilters?.[k] === true && !STRIP_FLAGS.includes(k))
            .map((k) => ({ key: k, keys: [k], label: DRILLDOWN_LABELS[k] })),
        ...(appliedFilters?.statusCategory ? [{ key: 'statusCategory', keys: ['statusCategory'],
            label: `Status group: ${appliedFilters.statusCategory.charAt(0)}${appliedFilters.statusCategory.slice(1).toLowerCase()}` }] : []),
        ...(appliedFilters?.ageFromDays != null || appliedFilters?.ageToDays != null ? [{ key: 'age', keys: ['ageFromDays', 'ageToDays'],
            label: appliedFilters?.ageToDays != null ? `Raised ${appliedFilters.ageFromDays || 0} to ${appliedFilters.ageToDays} days ago`
                : `Raised ${appliedFilters.ageFromDays}+ days ago` }] : []),
    ];
    const removeDrill = (keys) => {
        const next = { ...appliedFilters };
        keys.forEach((k) => delete next[k]);
        setPage(0);
        setAppliedFilters(next);
    };

    // Re-renders the per-row TAT badges once a minute.
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 60000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        const tabFilter = tabFilterFor(activeTicketTab);
        const fetchTickets = async () => {
            try {
                const request = { page, size: sizePerPage, ...appliedFilters, ...tabFilter, ...platformFilterFor(activePlatform) };
                const response = await postGeneric(apiConstants.ESCALATION_SEARCH_V2, request);
                if (!cancelled) setResData(response?.data || { results: [], total: 0 });
            } catch (error) {
                if (!cancelled) enqueueSnackbar("Some error occurred while loading tickets", { variant: "error" });
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        fetchTickets();
        return () => { cancelled = true; };
    }, [page, sizePerPage, appliedFilters, activeTicketTab, activePlatform, reloadTick, enqueueSnackbar]);

    // Both Open/Closed badges whichever is selected (for the selected platform), and the three
    // platform badges for the selected Open/Closed tab. One request for all of them — see
    // EscalationTabCounts (proship_mongo) for why this isn't separate /search calls.
    useEffect(() => {
        let cancelled = false;
        const fetchCounts = async () => {
            try {
                const response = await postGeneric(apiConstants.ESCALATION_TAB_COUNTS_V2,
                    { ...appliedFilters, ...tabFilterFor(activeTicketTab), ...platformFilterFor(activePlatform) });
                if (!cancelled) {
                    setOpenCount(response?.data?.openCount ?? null);
                    setClosedCount(response?.data?.closedCount ?? null);
                    setPlatformCounts(response?.data?.platformCounts ?? null);
                }
            } catch (error) {
                if (!cancelled) { setOpenCount(null); setClosedCount(null); setPlatformCounts(null); }
            }
        };
        fetchCounts();
        return () => { cancelled = true; };
    }, [appliedFilters, activeTicketTab, activePlatform, reloadTick]);

    // Quick close/reopen from the list — same pair of actions V1's TicketList.js offers,
    // targeting the first configured status in the CLOSED/OPEN category respectively. Asks
    // first, like V1 did (one click on a small icon shouldn't close a ticket).
    const quickStatusChange = async () => {
        const { row, targetCategory } = quickConfirm;
        const target = statusList.find((s) => s.category === targetCategory);
        if (!target) { setQuickConfirm(null); return; }
        setQuickProcessing(true);
        try {
            await patchGeneric(`${apiConstants.ESCALATION_DETAIL_V2}/${row.id}/status`, { status: target.label });
            enqueueSnackbar(`${row.escalationNumber} ${targetCategory === 'CLOSED' ? 'closed' : 'reopened'}`, { variant: "success" });
            setReloadTick((t) => t + 1);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Could not update status", { variant: "error" });
        } finally {
            setQuickProcessing(false);
            setQuickConfirm(null);
        }
    };

    // Bulk close/reopen — bounded to the current page selection (max 100), single synchronous
    // request, per-item result. Confirmation required since it's several tickets at once.
    const runBulkStatusChange = async () => {
        const targetCategory = bulkConfirm;
        setBulkProcessing(true);
        try {
            const response = await postGeneric(apiConstants.ESCALATION_BULK_STATUS_V2, {
                escalationIds: rowSelectionModel,
                targetCategory,
            });
            const { successCount, skippedCount, failedCount, total } = response?.data || {};
            enqueueSnackbar(
                `${targetCategory === 'CLOSED' ? 'Closed' : 'Reopened'} ${successCount}/${total} tickets` +
                (skippedCount ? ` — ${skippedCount} already ${targetCategory === 'CLOSED' ? 'closed' : 'open'}` : '') +
                (failedCount ? ` — ${failedCount} failed` : ''),
                { variant: failedCount ? 'warning' : 'success' }
            );
            setRowSelectionModel([]);
            setReloadTick((t) => t + 1);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Bulk update failed", { variant: "error" });
        } finally {
            setBulkProcessing(false);
            setBulkConfirm(null);
        }
    };

    // Built once per minute tick (for the TAT badges), not on every selection or filter-bar change.
    const columns = useMemo(() => [
        {
            field: 'escalationNumber', headerAlign: "left", headerName: 'Ticket #', width: 130, align: 'left',
            renderCell: ({ row }) => (
                <Box component={RouterLink} to={viewPath(row)}
                    sx={{ fontWeight: 800, fontSize: 13, color: COLORS.purple, textDecoration: 'none', borderRadius: '4px', whiteSpace: 'nowrap',
                        // Pin the colour for every link state: an inherited hover colour made the number blend into the row.
                        '&:visited, &:active': { color: COLORS.purple },
                        '&:hover': { color: '#4B2E70 !important', textDecoration: 'underline' },
                        '&:focus-visible': { outline: `2px solid ${COLORS.purple}`, outlineOffset: 2 } }}>
                    {row?.escalationNumber}
                </Box>
            ),
        },
        {
            field: 'platform', headerAlign: "left", headerName: 'Platform', width: 100, align: 'left', sortable: false,
            renderCell: ({ row }) => <TagPill label={PLATFORM_META[platformOf(row)].label} tone="neutral" maxWidth={90} />,
        },
        {
            field: 'awbNumber', headerAlign: "left", headerName: 'AWB', width: 150, align: 'left',
            renderCell: ({ row }) => row?.awbNumber
                ? <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: COLORS.ink, fontVariantNumeric: 'tabular-nums' }}>{row.awbNumber}</Typography>
                : dash,
        },
        {
            field: 'severity', headerAlign: "left", headerName: 'Severity', width: 112, align: 'left',
            renderCell: ({ row }) => SEVERITY_TONE[row?.severity] ? <SeverityPill severity={row.severity} /> : dash,
        },
        { field: 'merchantName', headerAlign: "left", headerName: 'Merchant', width: 150, align: 'left' },
        { field: 'courierName', headerAlign: "left", headerName: 'Courier', width: 140, align: 'left' },
        { field: 'createdByName', headerAlign: "left", headerName: 'Created By', width: 150, align: 'left' },
        {
            field: 'category', headerAlign: "left", headerName: 'Category', width: 180, align: 'left',
            renderCell: ({ row }) => row?.category ? <TagPill label={row.category} maxWidth={160} /> : dash,
        },
        {
            field: 'subCategory', headerAlign: "left", headerName: 'Sub Category', width: 200, align: 'left',
            renderCell: ({ row }) => row?.subCategory ? <TagPill label={row.subCategory} tone="neutral" maxWidth={180} /> : dash,
        },
        {
            field: 'firstResponseDueAt', headerAlign: "left", headerName: 'First Response Due', width: 150, align: 'left',
            renderCell: ({ row }) => row?.firstResponseDueAt ? parseDateAndTime(row.firstResponseDueAt) : dash,
        },
        {
            // The date as before, plus a badge for open tickets that are past TAT or due today.
            field: 'resolutionDueAt', headerAlign: "left", headerName: 'Resolution Due By', width: 170, align: 'left',
            renderCell: ({ row }) => row?.resolutionDueAt ? (
                <Box sx={{ display: 'grid', gap: 0.5, justifyItems: 'start', py: 0.25 }}>
                    <span>{parseDateAndTime(row.resolutionDueAt)}</span>
                    <TatBadge dueAt={row.resolutionDueAt} statusCategory={row.statusCategory} now={now} />
                </Box>
            ) : dash,
        },
        {
            field: 'status', headerAlign: "left", headerName: 'Status', width: 190, align: 'left',
            renderCell: ({ row }) => <StatusPill status={row?.status} category={row?.statusCategory} />,
        },
        {
            field: 'createdAt', headerAlign: "left", headerName: 'Created', width: 150, align: 'left',
            renderCell: ({ row }) => row?.createdAt ? new Date(row.createdAt).toLocaleString() : dash,
        },
        {
            field: 'updatedAt', headerAlign: "left", headerName: 'Last Updated', width: 150, align: 'left',
            renderCell: ({ row }) => row?.updatedAt ? new Date(row.updatedAt).toLocaleString() : dash,
        },
        {
            field: 'action', headerAlign: "center", headerName: 'Actions', width: 128, align: 'center', sortable: false,
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Tooltip title="View" arrow>
                        <IconButton size="small" aria-label={`View ${row.escalationNumber}`} onClick={() => navigate(viewPath(row))} sx={softIconBtnSx('purple')}>
                            <VisibilityIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={row.statusCategory !== 'CLOSED' ? "Close" : "Already closed"} arrow>
                        <span>
                            <IconButton size="small" aria-label={`Close ${row.escalationNumber}`} disabled={row.statusCategory === 'CLOSED'}
                                onClick={() => setQuickConfirm({ row, targetCategory: 'CLOSED' })} sx={softIconBtnSx('red')}>
                                <CancelIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={row.statusCategory === 'CLOSED' || row.statusCategory === 'RESOLVED' ? "Reopen" : "Not closed"} arrow>
                        <span>
                            <IconButton size="small" aria-label={`Reopen ${row.escalationNumber}`} disabled={row.statusCategory !== 'CLOSED' && row.statusCategory !== 'RESOLVED'}
                                onClick={() => setQuickConfirm({ row, targetCategory: 'OPEN' })} sx={softIconBtnSx('green')}>
                                <ReplayIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            ),
        },
    ], [now, navigate]);

    const rows = resData?.results || [];
    const total = resData?.total || 0;
    const scrollable = rows.length > SCROLL_AFTER_ROWS;
    const showSkeleton = isLoading && rows.length === 0;
    const showEmpty = !isLoading && rows.length === 0 && page === 0;
    const selectedCount = rowSelectionModel.length;
    const tabs = [
        { key: 'OPEN', label: 'Open', longLabel: 'Open Tickets', count: openCount },
        { key: 'CLOSED', label: 'Closed', longLabel: 'Closed Tickets', count: closedCount },
    ];

    return (
        <>
            <PageTitle>Escalation Management V2</PageTitle>

            <EmsPageHeader
                icon={SupportAgentIcon}
                title="Escalation Management V2"
                sub="Runs on the new in-house ticket backend, alongside Escalation Management, while the rest of the feature set is built out."
                actions={(
                    <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={() => setCreateOpen(true)}
                        sx={{ ...gradientBtnSx, borderRadius: '999px', px: 2.5, py: 1 }}>
                        Create Ticket
                    </Button>
                )}
            />

            <EscalationV2Nav />

            <EscalationFilterBar bar={bar} couriers={bar.couriers} onSubmit={onSubmit} onReset={onReset} hideFields={['platform']} />

            {/* All / Proship / WMS / CT: which system the tickets were raised from. */}
            <Box role="tablist" aria-label="Platform" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                {PLATFORM_TABS.map(({ key: p, longLabel }) => {
                    const active = activePlatform === p;
                    const count = !platformCounts ? null
                        : p === 'ALL' ? PLATFORMS.reduce((sum, k) => sum + (platformCounts[k] || 0), 0) : platformCounts[p];
                    return (
                        <Box key={p} component="button" type="button" role="tab" aria-selected={active} onClick={() => selectPlatform(p)}
                            sx={{ all: 'unset', boxSizing: 'border-box', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 1,
                                px: 2, py: 1, borderRadius: '12px', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap',
                                color: active ? COLORS.purple : COLORS.ink2, bgcolor: active ? COLORS.purpleSoft : '#fff',
                                border: `1px solid ${active ? COLORS.purple : COLORS.grid}`, transition: 'border-color 150ms, color 150ms',
                                '&:hover': active ? {} : { borderColor: COLORS.purple, color: COLORS.purple },
                                '&:focus-visible': { outline: `2px solid ${COLORS.purple}`, outlineOffset: 2 } }}>
                            {longLabel}
                            {count != null && (
                                <Box component="span" sx={{ minWidth: 22, textAlign: 'center', px: 0.875, borderRadius: '999px', fontSize: 12, fontWeight: 800,
                                    lineHeight: '20px', fontVariantNumeric: 'tabular-nums', bgcolor: active ? COLORS.purple : '#E9E5EF', color: active ? '#fff' : COLORS.ink2 }}>
                                    {count.toLocaleString('en-IN')}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>

            <TicketsSummaryStrip filters={{ ...appliedFilters, ...platformFilter }} activeKey={activeStripKey} openTabActive={activeTicketTab === 'OPEN'}
                onSelect={selectStrip} reloadTick={reloadTick} />

            {drillChips.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5, px: 2, py: 1.25, borderRadius: '12px',
                    bgcolor: COLORS.purpleSoft, border: `1px solid ${COLORS.purple}33` }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.purple }}>Showing only</Typography>
                    {drillChips.map((c) => (
                        <Box key={c.key} component="button" type="button" onClick={() => removeDrill(c.keys)} aria-label={`Remove filter ${c.label}`}
                            sx={{ all: 'unset', display: 'inline-flex', alignItems: 'center', gap: 0.5, pl: 1.25, pr: 0.75, py: 0.4, borderRadius: '999px', cursor: 'pointer',
                                bgcolor: '#fff', border: `1px solid ${COLORS.purple}55`, color: COLORS.purple, fontSize: 13, fontWeight: 600,
                                '&:hover': { borderColor: COLORS.purple }, '&:focus-visible': { outline: `2px solid ${COLORS.purple}` } }}>
                            {c.label}<Box component="span" aria-hidden sx={{ fontSize: 14, lineHeight: 1 }}>×</Box>
                        </Box>
                    ))}
                    <Typography sx={{ fontSize: 12, color: COLORS.ink3 }}>from an Analytics click. Remove one to see more tickets.</Typography>
                </Box>
            )}

            {/* ── List ── */}
            <Box sx={{ ...CARD_SX, p: 0, overflow: 'hidden', minWidth: 0, position: 'relative', ...rise(5),
                '&::before': { content: '""', position: 'absolute', left: 0, right: 0, top: 0, height: 3, background: ACCENTS.purple.grad, zIndex: 1 } }}>

                {/* Toolbar: Open / Closed (mirrors V1 EscalationManagement's tabs) + the page's bulk actions */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: { xs: 1.5, md: 2 }, pt: 2, pb: 1.5,
                    borderBottom: `1px solid ${COLORS.grid}` }}>
                    <Box role="tablist" aria-label="Ticket state"
                        sx={{ display: 'inline-flex', p: 0.5, gap: 0.5, borderRadius: '999px', bgcolor: '#F3F0F8', border: `1px solid #E7E1EF`, maxWidth: '100%' }}>
                        {tabs.map((tab) => {
                            const active = activeTicketTab === tab.key;
                            return (
                                <Box key={tab.key} component="button" type="button" role="tab" aria-selected={active}
                                    onClick={() => selectTicketTab(tab.key)}
                                    sx={{ all: 'unset', boxSizing: 'border-box', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 1,
                                        px: { xs: 1.5, sm: 2 }, py: 0.875, borderRadius: '999px', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap',
                                        color: active ? '#fff' : COLORS.ink2, background: active ? ACCENTS.purple.grad : 'transparent',
                                        boxShadow: active ? '0 4px 12px rgba(102,63,149,.28)' : 'none', transition: 'background-color 150ms, color 150ms',
                                        '&:hover': active ? {} : { color: COLORS.purple, bgcolor: '#fff' },
                                        '&:focus-visible': { outline: `2px solid ${COLORS.purple}`, outlineOffset: 2 } }}>
                                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{tab.longLabel}</Box>
                                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{tab.label}</Box>
                                    {tab.count != null && (
                                        <Box component="span" sx={{ minWidth: 22, textAlign: 'center', px: 0.875, borderRadius: '999px', fontSize: 12, fontWeight: 800, lineHeight: '20px',
                                            fontVariantNumeric: 'tabular-nums',
                                            bgcolor: active ? 'rgba(255,255,255,.22)' : tab.key === 'OPEN' ? ACCENTS.orange.soft : '#E9E5EF',
                                            color: active ? '#fff' : tab.key === 'OPEN' ? ACCENTS.orange.solid : COLORS.ink2 }}>
                                            {tab.count.toLocaleString('en-IN')}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    <Box sx={{ ml: { sm: 'auto' }, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                        <Button size="small" variant="outlined" startIcon={<PostAddIcon />} onClick={() => setBulkCreateOpen(true)} sx={pillBtnSx}>
                            Bulk Create
                        </Button>
                        {/* One sheet for comments, attachments and status changes (resolving included). */}
                        <Button size="small" variant="outlined" startIcon={<UploadFileIcon />} onClick={() => setBulkUpdateOpen(true)} sx={pillBtnSx}>
                            Bulk Update
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={() => setExportOpen(true)} sx={pillBtnSx}>
                            Export
                        </Button>
                    </Box>
                </Box>

                {/* Selection bar — the bulk status actions for the rows ticked on this page */}
                {selectedCount > 0 && (
                    <Box role="region" aria-label="Selected tickets" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', px: { xs: 1.5, md: 2 }, py: 1.25,
                        bgcolor: COLORS.purpleSoft, borderBottom: `1px solid #E2D7EE`, ...rise(0) }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 28, height: 28, px: 0.75, borderRadius: '999px',
                            background: ACCENTS.purple.grad, color: '#fff', fontSize: 13, fontWeight: 800 }}>{selectedCount}</Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.ink, mr: 0.5 }}>
                            {selectedCount === 1 ? 'ticket' : 'tickets'} selected
                        </Typography>
                        <Button size="small" variant="outlined" startIcon={<CancelIcon />} onClick={() => setBulkConfirm('CLOSED')}
                            sx={{ ...pillBtnSx, color: ACCENTS.red.solid, borderColor: '#F0BDBD', '&:hover': { borderColor: ACCENTS.red.solid, bgcolor: ACCENTS.red.soft } }}>
                            Bulk Close
                        </Button>
                        {/* Open-tab tickets are already open — reopening only makes sense on Closed. */}
                        {activeTicketTab === 'CLOSED' && (
                            <Button size="small" variant="outlined" startIcon={<ReplayIcon />} onClick={() => setBulkConfirm('OPEN')}
                                sx={{ ...pillBtnSx, color: ACCENTS.green.solid, borderColor: '#B5E2CF', '&:hover': { borderColor: ACCENTS.green.solid, bgcolor: ACCENTS.green.soft } }}>
                                Bulk Reopen
                            </Button>
                        )}
                        <Button size="small" onClick={() => setRowSelectionModel([])}
                            sx={{ textTransform: 'none', fontWeight: 700, fontSize: 13, color: COLORS.ink2, ml: 'auto', borderRadius: '999px' }}>
                            Clear selection
                        </Button>
                    </Box>
                )}

                {showSkeleton ? <SkeletonRows /> : showEmpty ? (
                    <EmptyState tab={activeTicketTab} drilled={!!activeStripKey} onClear={() => selectStrip(activeStripKey, true)} />
                ) : (
                    <Box sx={{ position: 'relative', minWidth: 0, ...(scrollable ? { height: { xs: 560, md: 'max(480px, calc(100vh - 160px))' } } : {}) }}>
                        {isLoading && <LoadingOverlay />}
                        <ProzoDataGrid
                            columns={columns}
                            rows={rows}
                            sizePerPage={sizePerPage}
                            setSizePerPage={setSizePerPage}
                            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            setPage={setPage}
                            page={page}
                            pagination={true}
                            hideFooterPagination={false}
                            paginationMode={"server"}
                            rowCount={total}
                            getRowId={(row) => row.id}
                            checkboxSelection
                            rowSelectionModel={rowSelectionModel}
                            setRowSelectionModel={setRowSelectionModel}
                            autoHeight={!scrollable}
                            getRowClassName={({ row }) => (tatState(row.resolutionDueAt, row.statusCategory, now)?.kind === 'past' ? 'ems-row-past' : '')}
                            sx={{ ...ROW_SX, ...(scrollable ? { height: '100%' } : {}) }}
                        />
                    </Box>
                )}
            </Box>

            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: DIALOG_PAPER_SX }}>
                <TicketsDialogTitle icon={ConfirmationNumberOutlinedIcon} title="Create Ticket" sub="Raise a new escalation" onClose={() => setCreateOpen(false)} />
                <DialogContent sx={{ pt: 1 }}>
                    <CreateEscalationTicket
                        onClose={() => setCreateOpen(false)}
                        onCreated={() => { setPage(0); setReloadTick((t) => t + 1); }}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={!!bulkConfirm} onClose={() => !bulkProcessing && setBulkConfirm(null)} maxWidth="xs" fullWidth PaperProps={{ sx: DIALOG_PAPER_SX }}>
                <TicketsDialogTitle icon={bulkConfirm === 'CLOSED' ? CancelIcon : ReplayIcon} accent={bulkConfirm === 'CLOSED' ? 'red' : 'green'}
                    title={`${bulkConfirm === 'CLOSED' ? 'Close' : 'Reopen'} ${selectedCount} ticket(s)?`} />
                <DialogContent>
                    <Typography sx={{ fontSize: 14, color: COLORS.ink2 }}>
                        This will {bulkConfirm === 'CLOSED' ? 'close' : 'reopen'} all {selectedCount} selected
                        tickets. This can't be undone from here in bulk — you'd need to change them back one by one.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setBulkConfirm(null)} disabled={bulkProcessing} sx={{ textTransform: 'none', fontWeight: 600, color: COLORS.ink2 }}>
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained" color={bulkConfirm === 'CLOSED' ? 'error' : 'success'}
                        loading={bulkProcessing} onClick={runBulkStatusChange} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px' }}
                    >
                        Confirm
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <Dialog open={!!quickConfirm} onClose={() => !quickProcessing && setQuickConfirm(null)} maxWidth="xs" fullWidth PaperProps={{ sx: DIALOG_PAPER_SX }}>
                <TicketsDialogTitle icon={quickConfirm?.targetCategory === 'CLOSED' ? CancelIcon : ReplayIcon} accent={quickConfirm?.targetCategory === 'CLOSED' ? 'red' : 'green'}
                    title={`${quickConfirm?.targetCategory === 'CLOSED' ? 'Close' : 'Reopen'} ${quickConfirm?.row?.escalationNumber || ''}?`} />
                <DialogContent>
                    <Typography sx={{ fontSize: 14, color: COLORS.ink2 }}>
                        {quickConfirm?.targetCategory === 'CLOSED'
                            ? 'The ticket will be closed. If the requester is external, they get an email about it.'
                            : 'The ticket will be reopened and marked as reopened. If the requester is external, they get an email about it.'}
                    </Typography>
                    {quickConfirm?.targetCategory === 'CLOSED' && tatState(quickConfirm?.row?.resolutionDueAt, quickConfirm?.row?.statusCategory, now)?.kind === 'past' && (
                        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center', fontSize: 13, color: ACCENTS.orange.solid }}>
                            <WarningAmberRoundedIcon sx={{ fontSize: 18 }} aria-hidden />This ticket is already past its resolution TAT.
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setQuickConfirm(null)} disabled={quickProcessing} sx={{ textTransform: 'none', fontWeight: 600, color: COLORS.ink2 }}>
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained" color={quickConfirm?.targetCategory === 'CLOSED' ? 'error' : 'success'}
                        loading={quickProcessing} onClick={quickStatusChange} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px' }}
                    >
                        {quickConfirm?.targetCategory === 'CLOSED' ? 'Close ticket' : 'Reopen ticket'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <BulkCreateModal
                open={bulkCreateOpen}
                onClose={() => setBulkCreateOpen(false)}
                onDone={() => setReloadTick((t) => t + 1)}
            />
            <BulkUpdateModal
                open={bulkUpdateOpen}
                onClose={() => setBulkUpdateOpen(false)}
                onDone={() => setReloadTick((t) => t + 1)}
                statusList={statusList}
            />
            <ExportTicketsModal
                open={exportOpen}
                onClose={() => setExportOpen(false)}
                // The same tickets as the list: its filters plus the Open/Closed and platform tabs.
                filters={{ ...appliedFilters, ...tabFilterFor(activeTicketTab), ...platformFilter }}
            />
        </>
    );
};

export default EscalationManagementV2;
